/**
 * ApBillVoucher — Printable transaction voucher for AP Bills.
 *
 * Rendered inside MasterReportPreview (which handles the print modal,
 * @media print isolation, and the Print button).
 */

import type { ReactNode } from 'react';
import type { ApBillDto, ApBillLineDto } from '@/types/apBill.types';
import { PrintableTable } from '@/components/master/report/PrintableTable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.6 }}>
    <span style={{ color: '#6b7280', minWidth: '130px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const colour: Record<string, { bg: string; text: string }> = {
    Draft:          { bg: '#f3f4f6', text: '#374151' },
    Submitted:      { bg: '#dbeafe', text: '#1e40af' },
    PendingApproval:{ bg: '#fef9c3', text: '#854d0e' },
    Approved:       { bg: '#d1fae5', text: '#065f46' },
    Rejected:       { bg: '#fee2e2', text: '#991b1b' },
    Posted:         { bg: '#a7f3d0', text: '#064e3b' },
    Cancelled:      { bg: '#f3f4f6', text: '#6b7280' },
    Reversed:       { bg: '#ede9fe', text: '#5b21b6' },
  };
  const c = colour[status] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
    }}>
      {status}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface ApBillVoucherProps {
  bill:        ApBillDto;
  companyName: string;
  printedBy?:  string;
}

export const ApBillVoucher = ({
  bill,
  companyName,
  printedBy,
}: ApBillVoucherProps): ReactNode => {

  const lineColumns = [
    { key: 'lineNumber',  label: '#',            width: '28px',  align: 'center' as const,
      render: (l: ApBillLineDto) => l.lineNumber },
    { key: 'description', label: 'Description',
      render: (l: ApBillLineDto) => l.description || '' },
    { key: 'account',     label: 'Account',       width: '110px',
      render: (l: ApBillLineDto) => (
        <span>
          <span style={{ fontFamily: 'monospace', fontSize: '9px', color: '#2563eb', marginRight: '4px' }}>
            {l.accountCode}
          </span>
          <span style={{ color: '#6b7280', fontSize: '9px' }}>{l.accountName}</span>
        </span>
      )},
    { key: 'quantity',    label: 'Qty',           width: '50px',  align: 'right' as const,
      render: (l: ApBillLineDto) => fmtNum(l.quantity, 2) },
    { key: 'unitPrice',   label: 'Unit Price',    width: '80px',  align: 'right' as const,
      render: (l: ApBillLineDto) => fmtNum(l.unitPrice) },
    { key: 'discountPct', label: 'Disc %',        width: '52px',  align: 'right' as const,
      render: (l: ApBillLineDto) => l.discountPercent > 0 ? fmtNum(l.discountPercent) + '%' : '' },
    { key: 'lineAmount',  label: 'Net Amt',       width: '80px',  align: 'right' as const,
      render: (l: ApBillLineDto) => fmtNum(l.lineAmount) },
    { key: 'taxRate',     label: 'Tax %',         width: '52px',  align: 'right' as const,
      render: (l: ApBillLineDto) => l.taxRate > 0 ? fmtNum(l.taxRate) + '%' : '' },
    { key: 'taxAmount',   label: 'Tax Amt',       width: '80px',  align: 'right' as const,
      render: (l: ApBillLineDto) => fmtNum(l.taxAmount) },
    { key: 'totalAmount', label: 'Total',         width: '84px',  align: 'right' as const,
      render: (l: ApBillLineDto) => <strong>{fmtNum(l.totalAmount)}</strong> },
    { key: 'remarks',     label: 'Remarks',       width: '80px',
      render: (l: ApBillLineDto) => l.remarks || '' },
  ];

  const workflowRows = [
    bill.submittedBy  ? { role: 'Submitted By',  name: bill.submittedBy,  date: bill.submittedDate  } : null,
    bill.approvedBy   ? { role: 'Approved By',   name: bill.approvedBy,   date: bill.approvedDate   } : null,
    bill.postedBy     ? { role: 'Posted By',      name: bill.postedBy,     date: bill.postedDate     } : null,
  ].filter(Boolean) as { role: string; name: string; date: string | null | undefined }[];

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>

      {/* ── Voucher Header ──────────────────────────────────────────────────── */}
      <div style={{
        borderBottom: '2px solid #1e3a5f',
        paddingBottom: '12px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
              myLedger ONE
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e3a5f', lineHeight: 1.2 }}>
              {bill.isReversal ? 'Reversal Bill' : 'Supplier Bill Voucher'}
            </div>
            <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
              {companyName}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
              {bill.billNumber}
            </div>
            <div style={{ marginTop: '4px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
              <StatusBadge status={bill.workflowStatus} />
              {bill.isReversal && (
                <span style={{
                  display: 'inline-block', padding: '1px 7px', borderRadius: '10px',
                  fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                  background: '#fef3c7', color: '#92400e',
                }}>
                  Reversal Bill
                </span>
              )}
            </div>
            <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px' }}>
              Printed: {new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {printedBy && ` · ${printedBy}`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Supplier + Document details ─────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 32px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Supplier
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{bill.supplierName}</div>
          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>{bill.supplierCode}</div>
          {bill.supplierInvoiceNumber && (
            <div style={{ fontSize: '10px', color: '#374151', marginTop: '4px' }}>
              <span style={{ color: '#6b7280' }}>Supplier Invoice#:</span> {bill.supplierInvoiceNumber}
            </div>
          )}
          {bill.reference && (
            <div style={{ fontSize: '10px', color: '#374151', marginTop: '2px' }}>
              <span style={{ color: '#6b7280' }}>Reference:</span> {bill.reference}
            </div>
          )}
        </div>
        <div>
          <LabelValue label="Bill Date"      value={fmtDate(bill.billDate)} />
          <LabelValue label="Posting Date"   value={fmtDate(bill.postingDate)} />
          {bill.dueDate && (
            <LabelValue label="Due Date"     value={fmtDate(bill.dueDate)} />
          )}
          <LabelValue label="Currency"       value={`${bill.currencyCode} (Rate: ${bill.exchangeRate.toFixed(4)})`} />
          <LabelValue label="Period"         value={`${bill.financialPeriodCode} / ${bill.financialYearCode}`} />
          <LabelValue label="Posting Status" value={bill.postingStatus} />
        </div>
      </div>

      {/* Narration */}
      {bill.narration && (
        <div style={{
          marginBottom: '16px',
          padding: '8px 12px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#374151',
        }}>
          <span style={{ color: '#6b7280', fontWeight: 600 }}>Narration: </span>
          {bill.narration}
        </div>
      )}

      {/* ── Lines table ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Bill Lines
      </div>

      <PrintableTable
        columns={lineColumns}
        rows={bill.lines ?? []}
        rowKey={(l) => l.id}
      />

      {/* ── Totals ──────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '4px',
        marginBottom: '20px',
      }}>
        <div style={{ width: '220px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '10px' }}>
            <span style={{ color: '#6b7280' }}>Sub Total:</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtNum(bill.subTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '10px' }}>
            <span style={{ color: '#6b7280' }}>Tax Amount:</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtNum(bill.taxAmount)}</span>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '5px 8px',
            marginTop: '4px',
            background: '#1e3a5f',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: 'white',
          }}>
            <span>Total ({bill.currencyCode}):</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtNum(bill.totalAmount)}</span>
          </div>
          {bill.exchangeRate !== 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>
              <span>Base Amount:</span>
              <span style={{ fontFamily: 'monospace' }}>{fmtNum(bill.baseAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Workflow Trail ───────────────────────────────────────────────────── */}
      {workflowRows.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Workflow Trail
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {workflowRows.map((row) => (
                <tr key={row.role} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 8px', color: '#374151' }}>{row.role}</td>
                  <td style={{ padding: '4px 8px', color: '#111827', fontWeight: 500 }}>{row.name}</td>
                  <td style={{ padding: '4px 8px', color: '#374151', fontFamily: 'monospace' }}>{fmtDate(row.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Signature blocks ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginTop: '32px',
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
      }}>
        {[
          { label: 'Prepared By' },
          { label: 'Approved By' },
          { label: 'Posted By' },
        ].map(({ label }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              paddingTop: '4px',
              fontSize: '9px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
